import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { LOG_EVENTS } from '../../../../core/services/log-events';
import { LoggingService } from '../../../../core/services/logging.service';
import {
  HlmAlertComponent,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
  HlmRadioComponent,
  HlmRadioGroupComponent,
  HlmTextareaDirective,
} from '../../../../ui';
import {
  Feedback,
  FeedbackAnswers,
  FeedbackOption,
  FeedbackQuestion,
  Questionnaire,
} from './transcript-feedback.model';
import {
  isStepComplete,
  keepKnownAnswers,
  pruneAnswers,
  summarize,
  visibleQuestions,
  visibleSteps,
} from './transcript-feedback-steps';
import { TranscriptFeedbackService } from './transcript-feedback.service';

type LoadState = 'loading' | 'hidden' | 'error' | 'ready';

const SEND_FAILED = 'Could not send your feedback. Please try again.';
/** Same text as call-evaluation's bar. */
const QUESTIONS_CHANGED = 'The questions were updated. Please check your answers and send again.';

/**
 * Transcript feedback bar. Everything renders from the questionnaire the API returns.
 * The same bar exists in call-evaluation: keep both in sync.
 *
 * @ticket QAS-6936
 */
@Component({
  selector: 'cmt-transcript-feedback-bar',
  standalone: true,
  imports: [
    HlmAlertComponent,
    HlmBtnDirective,
    HlmCheckboxComponent,
    HlmIconComponent,
    HlmRadioComponent,
    HlmRadioGroupComponent,
    HlmTextareaDirective,
  ],
  templateUrl: './transcript-feedback-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex-none' },
})
export class TranscriptFeedbackBarComponent {
  readonly cchsid = input.required<string>();
  readonly callDirection = input<'inbound' | 'outbound' | null>(null);
  /** Customer of the call; part of the usage audit fields when known. */
  readonly customerId = input<string | null>(null);

  private readonly transcriptFeedbackService = inject(TranscriptFeedbackService);
  private readonly loggingService = inject(LoggingService);

  protected readonly loadState = signal<LoadState>('loading');
  protected readonly questionnaire = signal<Questionnaire | null>(null);
  protected readonly saved = signal<Feedback | null>(null);
  protected readonly editing = signal(false);
  protected readonly open = signal(true);
  protected readonly answers = signal<FeedbackAnswers>({});
  protected readonly comment = signal('');
  protected readonly step = signal(1);
  protected readonly sending = signal(false);
  protected readonly sendError = signal<string | null>(null);
  /** The reload after a 409 is a notice, not a failure. */
  protected readonly sendErrorTone = computed<'info' | 'error'>(() =>
    this.sendError() === QUESTIONS_CHANGED ? 'info' : 'error',
  );
  private startedAt: number | null = null;
  private sendSub: Subscription | null = null;

  protected readonly questions = computed(() => this.questionnaire()?.questions ?? []);
  protected readonly showForm = computed(() => this.editing() || !this.saved());
  protected readonly steps = computed(() => visibleSteps(this.questions(), this.answers()));
  protected readonly stepIdx = computed(() => Math.max(0, this.steps().indexOf(this.step())));
  protected readonly currentStep = computed(() => this.steps()[this.stepIdx()] ?? 1);
  /** Before the first question is answered only a disabled Next shows (no comment, no Send). */
  protected readonly firstAnswered = computed(() => {
    const first = this.questions()[0];
    return !!first && (this.answers()[first.key] ?? []).length > 0;
  });
  protected readonly isLast = computed(
    () => this.stepIdx() === this.steps().length - 1 && this.firstAnswered(),
  );
  protected readonly complete = computed(() =>
    isStepComplete(this.questions(), this.answers(), this.currentStep()),
  );
  protected readonly stepQuestions = computed(() =>
    visibleQuestions(this.questions(), this.answers()).filter((q) => q.step === this.currentStep()),
  );
  protected readonly savedSummary = computed(() => {
    const fb = this.saved();
    return fb ? summarize(this.questions(), fb.answers) : '';
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.sendSub?.unsubscribe());
    // Reload with fresh state whenever the call changes.
    effect((onCleanup) => {
      const id = this.cchsid();
      const sub = untracked(() => {
        this.resetState();
        return forkJoin([
          this.transcriptFeedbackService.findQuestionnaire(),
          this.transcriptFeedbackService.findMyFeedback(id),
        ]).subscribe({
          next: ([questionnaire, fb]) => {
            this.questionnaire.set(questionnaire);
            // A missing-transcript report is not a rating: a later rating replaces it, so show the form.
            this.saved.set(fb?.kind === 'rating' ? fb : null);
            this.loadState.set('ready');
          },
          error: (err: unknown) =>
            this.loadState.set(
              err instanceof HttpErrorResponse && err.status === 403 ? 'hidden' : 'error',
            ),
        });
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  protected isSelected(q: FeedbackQuestion, option: FeedbackOption): boolean {
    return (this.answers()[q.key] ?? []).includes(option.key);
  }

  /** Same row look as call-evaluation's bar: a picked answer gets a light primary background. */
  protected optionClass(q: FeedbackQuestion, option: FeedbackOption): string {
    return this.isSelected(q, option)
      ? 'rounded px-1.5 py-1 bg-primary/10'
      : 'rounded px-1.5 py-1 hover:bg-muted';
  }

  protected selectedKey(q: FeedbackQuestion): string | null {
    return (this.answers()[q.key] ?? [])[0] ?? null;
  }

  protected setAnswer(q: FeedbackQuestion, optionKey: string): void {
    if (this.startedAt === null) this.startedAt = Date.now();
    this.sendError.set(null);
    this.answers.update((prev) => {
      const current = prev[q.key] ?? [];
      if (q.type === 'checkboxes') {
        return {
          ...prev,
          [q.key]: current.includes(optionKey)
            ? current.filter((k) => k !== optionKey)
            : [...current, optionKey],
        };
      }
      return { ...prev, [q.key]: [optionKey] };
    });
  }

  protected toggleOpen(): void {
    this.open.update((o) => !o);
  }

  protected onComment(event: Event): void {
    this.comment.set((event.target as HTMLTextAreaElement).value);
  }

  protected next(): void {
    this.sendError.set(null);
    this.step.set(this.steps()[this.stepIdx() + 1] ?? this.step());
  }

  protected back(): void {
    this.sendError.set(null);
    this.step.set(this.steps()[this.stepIdx() - 1] ?? this.step());
  }

  protected startChange(): void {
    this.answers.set(keepKnownAnswers(this.questions(), this.saved()?.answers ?? {}));
    this.comment.set(this.saved()?.comment ?? '');
    this.step.set(1);
    this.sendError.set(null);
    this.startedAt = null;
    this.editing.set(true);
  }

  protected cancel(): void {
    this.editing.set(false);
    this.sendError.set(null);
  }

  protected send(): void {
    const questionnaire = this.questionnaire();
    if (this.sending() || !questionnaire) return;
    this.sending.set(true);
    this.sendError.set(null);
    // Not measured when a Change is sent without touching an answer: send no value rather than 0.
    const startedAt = this.startedAt;
    const timing =
      startedAt === null
        ? {}
        : { context: { firstAnswerToSendSec: Math.max(0, (Date.now() - startedAt) / 1000) } };
    const cchsid = this.cchsid();
    const direction = this.callDirection();
    const customerId = this.customerId();
    this.sendSub = this.transcriptFeedbackService
      .putMyFeedback(cchsid, {
        sourceApp: 'amie',
        questionnaireVersion: questionnaire.version,
        callDirection: direction,
        callGroupType: null,
        transcriptionLanguageCode: null,
        answers: pruneAnswers(questionnaire.questions, this.answers()),
        comment: this.comment().trim() || null,
        ...timing,
      })
      .subscribe({
        next: (fb) => {
          this.saved.set(fb);
          this.editing.set(false);
          this.sending.set(false);
          // Call audit fields only: never the comment or the answers.
          this.loggingService.usage(LOG_EVENTS.transcriptFeedbackSend, {
            cchsid,
            ...(customerId != null ? { customerId } : {}),
            direction,
          });
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.reloadQuestions();
            return;
          }
          this.sendError.set(this.sendErrorMessage(err));
          this.sending.set(false);
        },
      });
  }

  /**
   * aida rejected the rating because the questionnaire changed (409, nothing stored).
   * Load the new questions, keep the answers they still know and the comment, and let
   * the user check them from the first step.
   */
  private reloadQuestions(): void {
    this.sendSub = this.transcriptFeedbackService.findQuestionnaire().subscribe({
      next: (questionnaire) => {
        this.questionnaire.set(questionnaire);
        this.answers.set(keepKnownAnswers(questionnaire.questions, this.answers()));
        this.step.set(1);
        this.editing.set(true);
        this.sendError.set(QUESTIONS_CHANGED);
        this.sending.set(false);
      },
      error: () => {
        this.sendError.set(SEND_FAILED);
        this.sending.set(false);
      },
    });
  }

  /** aida's readable 4xx `detail` is shown as is; 5xx, timeouts and network errors get a short line. */
  private sendErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500) {
      const detail = (err.error as { detail?: unknown } | null)?.detail;
      if (typeof detail === 'string' && detail) return detail;
    }
    return SEND_FAILED;
  }

  private resetState(): void {
    // A pending Send belongs to the previous call: its late reply must not touch this state.
    this.sendSub?.unsubscribe();
    this.sendSub = null;
    this.loadState.set('loading');
    this.questionnaire.set(null);
    this.saved.set(null);
    this.editing.set(false);
    this.open.set(true);
    this.answers.set({});
    this.comment.set('');
    this.step.set(1);
    this.sending.set(false);
    this.sendError.set(null);
    this.startedAt = null;
  }
}
